import { useContext, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  Building2,
  CircleDollarSignIcon,
  InfoIcon,
  PackageIcon,
  ReceiptIcon,
  Settings2,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { Load } from 'types/Load';
import { Undef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import {
  getPlsLoadEditURL,
  plsCreateUsedTestingEnvironment,
} from 'utils/plsLoadEditUrl';

import LoadBuildingAccordionControls from '../components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from '../components/LoadCreatedMessage';
import { BillToSectionForm } from './BillTo';
import { CustomerSectionForm } from './Customer';
import { LoadItemsSection } from './LoadItemsSection';
import { OperationalFieldsSection } from './OperationalFields';
import { OptionalFieldsSection } from './OptionalFields';
import { PlsRouteSection } from './PlsRouteSection';
import { RatesSection } from './Rates';
import { SpecificationsForm } from './Specifications';
import { AvailableTabs, PLS_SECTION_LABELS } from './constants';
import { usePlsFormLogic } from './hooks/usePlsFormLogic';

interface UnifiedPlsLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
}

export default function UnifiedPlsLoadForm({
  isCreateMode,
  load,
}: UnifiedPlsLoadFormProps) {
  const { tmsIntegrations } = useServiceFeatures();
  // Prefer prod (non-testing) PLS for catalog/customer/ref lookups. CreateLoad
  // remaps pilot creates on the backend; update payloads use load.tmsID in
  // usePlsFormLogic so staging-created loads stay on staging.
  const tmsID =
    tmsIntegrations?.find(
      (integration) =>
        integration.name === TMS.PLS && !integration.isTestingEnvironment
    )?.id ??
    tmsIntegrations?.find((integration) => integration.name === TMS.PLS)?.id;

  const {
    currentState: { threadId, emailId, threadItemId, openExternalUrl },
  } = useContext(SidebarStateContext);

  const {
    formMethods,
    plsData,
    operationalFields,
    referenceCatalogs,
    state: {
      activeTabs,
      setActiveTabs,
      expandAll,
      collapseAll,
      defaultView,
      setDefaultView,
      allTabs,
      isLoading,
      builtLoad,
      showAIHints,
      suggestedCustomerExternalTMSID,
      suggestedBillToExternalTMSID,
    },
    actions: { onSubmit, onInvalid, handleClearForm },
  } = usePlsFormLogic({
    isCreateMode,
    load,
    tmsID,
    threadId,
    emailId,
    threadItemId,
  });

  const [autoBillToId, setAutoBillToId] = useState<Undef<string>>();
  const watchedCustomerId = formMethods.watch('customer.externalTMSID');
  const activeSuggestedBillToId =
    suggestedCustomerExternalTMSID === watchedCustomerId
      ? suggestedBillToExternalTMSID
      : undefined;

  const submitButtonLabel = isCreateMode
    ? ButtonText.BuildLoad
    : ButtonText.UpdateTMS;
  const submitButtonName = isCreateMode
    ? ButtonNamePosthog.BuildLoad
    : ButtonNamePosthog.UpdateTMS;

  return (
    <div className='mb-5'>
      <TooltipProvider>
        <ExtendedFormProvider
          aiDefaultValues={showAIHints}
          aiIconOnly={true}
          highlightDirtyFields={!isCreateMode}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit, onInvalid)}>
              <LoadBuildingAccordionControls
                activeTabs={activeTabs}
                allTabs={allTabs}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                defaultView={defaultView}
                onDefaultViewChange={setDefaultView}
              />

              <Accordion
                type='multiple'
                value={activeTabs}
                onValueChange={setActiveTabs}
              >
                <LoadSectionAccordionItem
                  label='Customer'
                  icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.customer}
                  activeTabs={activeTabs}
                >
                  <CustomerSectionForm
                    formMethods={formMethods}
                    tmsID={tmsID}
                    customers={plsData.customers}
                    setCustomers={plsData.setCustomers}
                    customerLocations={plsData.locations}
                    setCustomerLocations={plsData.setLocations}
                    customerLocationCatalog={plsData.locationCatalog}
                    locationCatalogCustomerId={
                      plsData.locationCatalogCustomerId
                    }
                    isLoadingCustomers={plsData.isLoading.customers}
                    isLoadingCustomerLocations={plsData.isLoading.locations}
                    setIsLoadingCustomers={plsData.setIsLoadingCustomers}
                    refreshCustomerLocations={plsData.refreshLocations}
                    suggestedCustomerExternalTMSID={
                      suggestedCustomerExternalTMSID
                    }
                    suggestedBillToExternalTMSID={suggestedBillToExternalTMSID}
                    autoBillToId={autoBillToId}
                    setAutoBillToId={setAutoBillToId}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Bill To'
                  icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.billTo}
                  activeTabs={activeTabs}
                >
                  <BillToSectionForm
                    formMethods={formMethods}
                    billTos={plsData.billTos}
                    isLoadingBillTos={plsData.isLoading.billTos}
                    handleBillToSearch={plsData.handleBillToSearch}
                    refreshBillTos={plsData.refreshBillTos}
                    isBillToSearchApiEnabled={plsData.isBillToSearchApiEnabled}
                    isCreateMode={isCreateMode}
                    aiHighlightedExternalTMSID={
                      activeSuggestedBillToId || autoBillToId
                    }
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Specs'
                  icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.specifications}
                  activeTabs={activeTabs}
                >
                  <SpecificationsForm
                    formMethods={formMethods}
                    tmsID={tmsID}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Items'
                  icon={<PackageIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.items}
                  activeTabs={activeTabs}
                >
                  <LoadItemsSection formMethods={formMethods} />
                </LoadSectionAccordionItem>

                <PlsRouteSection
                  formMethods={formMethods}
                  activeTabs={activeTabs}
                />

                <LoadSectionAccordionItem
                  label='Rates'
                  icon={
                    <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                  }
                  name={AvailableTabs.rates}
                  activeTabs={activeTabs}
                >
                  <RatesSection
                    formMethods={formMethods}
                    tmsID={tmsID}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label={PLS_SECTION_LABELS.operationalFields}
                  icon={<Settings2 className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.operationalFields}
                  activeTabs={activeTabs}
                >
                  <OperationalFieldsSection
                    formMethods={formMethods}
                    fieldDefinitions={operationalFields.fieldDefinitions}
                    isLoading={operationalFields.isLoading}
                    error={operationalFields.error}
                    isCreateMode={isCreateMode}
                  />
                </LoadSectionAccordionItem>

                <LoadSectionAccordionItem
                  label='Optional'
                  icon={<InfoIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.optional}
                  activeTabs={activeTabs}
                >
                  <OptionalFieldsSection
                    formMethods={formMethods}
                    tmsID={tmsID}
                    loadReferenceFields={referenceCatalogs.loadReferenceFields}
                    isLoadingLoadReferenceFields={
                      referenceCatalogs.isLoading.loadReferenceFields
                    }
                    loadReferenceFieldsError={
                      referenceCatalogs.errors.loadReferenceFields
                    }
                    operationalFieldDefinitions={
                      operationalFields.fieldDefinitions
                    }
                  />
                </LoadSectionAccordionItem>
              </Accordion>

              <section className='w-full mt-4'>
                <Button
                  buttonNamePosthog={submitButtonName}
                  type='submit'
                  className='w-full'
                  disabled={isLoading}
                  data-testid={
                    isCreateMode ? 'create-load-submit' : 'update-load-submit'
                  }
                >
                  {isLoading ? <ButtonLoader /> : submitButtonLabel}
                </Button>

                {isCreateMode && formMethods.formState.isDirty && (
                  <Flex justify='center' align='center'>
                    <Button
                      type='button'
                      className='w-50% mt-4 h-8 text-sm text-neutral-500'
                      variant='outline'
                      onClick={handleClearForm}
                      buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    >
                      {ButtonText.ClearForm}
                    </Button>
                  </Flex>
                )}

                {isCreateMode && builtLoad ? (
                  <PlsCreatedLoadBanner
                    builtLoad={builtLoad}
                    isTestingEnvironment={plsCreateUsedTestingEnvironment(
                      tmsIntegrations,
                      builtLoad.tmsID
                    )}
                    openExternalUrl={openExternalUrl}
                  />
                ) : null}
              </section>
            </form>
          </FormProvider>
        </ExtendedFormProvider>
      </TooltipProvider>
    </div>
  );
}

function PlsCreatedLoadBanner({
  builtLoad,
  isTestingEnvironment,
  openExternalUrl,
}: {
  builtLoad: Load;
  isTestingEnvironment: boolean;
  openExternalUrl: (url: string) => void;
}) {
  const builtLoadId =
    builtLoad.freightTrackingID?.trim() ||
    builtLoad.externalTMSID?.trim() ||
    '';
  if (!builtLoadId) {
    return null;
  }

  const url = builtLoad.externalTMSID
    ? getPlsLoadEditURL(builtLoad.externalTMSID, isTestingEnvironment)
    : null;

  return (
    <>
      <LoadCreatedMessage
        builtLoadId={builtLoadId}
        poNumber={builtLoad.poNums ?? undefined}
      />
      {url ? (
        <Typography
          variant='body-sm'
          className='mb-1 cursor-pointer underline'
          data-testid='created-load-link'
          onClick={() => openExternalUrl(url)}
        >
          Access the created load for more details
        </Typography>
      ) : null}
    </>
  );
}
